/**
 * SPQR Micro-Interactions — GSAP hover, click & state animations
 * Adds tactile, luxury-feel interactions across the entire store.
 * Loaded conditionally via settings.animations_gsap_enable.
 *
 * Uses event delegation on document for repeated elements (cards, links).
 * Only runs on devices with hover capability (no touch-only).
 * Respects prefers-reduced-motion.
 */

(function () {
  'use strict';

  if (typeof gsap === 'undefined') return;

  // ─── Gates ──────────────────────────────────────────────
  var isTouch = !window.matchMedia('(hover: hover)').matches;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) return;

  var isEditor = typeof Shopify !== 'undefined' && Shopify.designMode;

  // ─── Easing presets for luxury feel ─────────────────────
  var SPRING = 'elastic(1, 0.5)';
  var SMOOTH = 'power2.out';
  var SNAP = 'power3.out';

  // ─── Helpers ────────────────────────────────────────────
  function closest(el, selector) {
    while (el && el !== document) {
      if (el.matches && el.matches(selector)) return el;
      el = el.parentElement;
    }
    return null;
  }

  // ─── 1. BUTTON PRESS FEEDBACK ──────────────────────────
  // All .element-button get tactile press: scale down on press, spring back on release.
  // Also: subtle hover lift.

  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var btn = closest(e.target, '.element-button');
      if (!btn) return;
      gsap.to(btn, { y: -2, duration: 0.25, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var btn = closest(e.target, '.element-button');
      if (!btn) return;
      gsap.to(btn, { y: 0, duration: 0.3, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  document.addEventListener('mousedown', function (e) {
    var btn = closest(e.target, '.element-button');
    if (!btn) return;
    gsap.to(btn, { scale: 0.97, duration: 0.1, ease: 'power2.in', overwrite: 'auto' });
  }, true);

  document.addEventListener('mouseup', function (e) {
    var btn = closest(e.target, '.element-button');
    if (!btn) return;
    gsap.to(btn, { scale: 1, duration: 0.5, ease: SPRING, overwrite: 'auto' });
  }, true);

  // ─── 2. ATC / BUY BUTTON ENHANCED HOVER ───────────────
  // Stronger lift + press for submit buttons
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var btn = closest(e.target, '.element-button[type="submit"]');
      if (!btn) return;
      gsap.to(btn, { y: -3, duration: 0.25, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var btn = closest(e.target, '.element-button[type="submit"]');
      if (!btn) return;
      gsap.to(btn, { y: 0, duration: 0.3, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mousedown', function (e) {
      var btn = closest(e.target, '.element-button[type="submit"]');
      if (!btn) return;
      gsap.to(btn, { scale: 0.96, duration: 0.1, ease: 'power2.in', overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseup', function (e) {
      var btn = closest(e.target, '.element-button[type="submit"]');
      if (!btn) return;
      gsap.to(btn, { scale: 1, duration: 0.6, ease: SPRING, overwrite: 'auto' });
    }, true);
  }

  // ─── 3. NAVIGATION LINK UNDERLINE ─────────────────────
  if (!isTouch) {
    var navLinks = document.querySelectorAll('.site-nav__link');
    navLinks.forEach(function (link) {
      // Create an underline pseudo-element effect via scaleX
      var tl = gsap.timeline({ paused: true });
      tl.fromTo(link, { '--nav-underline-scale': 0 }, {
        '--nav-underline-scale': 1, duration: 0.3, ease: SNAP
      });
      link.addEventListener('mouseenter', function () { tl.play(); });
      link.addEventListener('mouseleave', function () { tl.reverse(); });
    });
  }

  // ─── 4. FOOTER LINKS ──────────────────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var link = closest(e.target, '.site-footer__linklist a, .footer__link');
      if (!link) return;
      gsap.to(link, { x: 3, duration: 0.25, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var link = closest(e.target, '.site-footer__linklist a, .footer__link');
      if (!link) return;
      gsap.to(link, { x: 0, duration: 0.3, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 5. SOCIAL MEDIA ICONS ────────────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var icon = closest(e.target, '.social-icons a, .footer__social a');
      if (!icon) return;
      gsap.to(icon, { scale: 1.15, rotation: 5, duration: 0.3, ease: SPRING, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var icon = closest(e.target, '.social-icons a, .footer__social a');
      if (!icon) return;
      gsap.to(icon, { scale: 1, rotation: 0, duration: 0.4, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 6. CART BADGE BOUNCE ─────────────────────────────
  // Watch for the cart badge becoming visible and bounce it.
  var cartBubble = document.querySelector('.cart-link__bubble');
  if (cartBubble) {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          if (cartBubble.classList.contains('cart-link__bubble--visible')) {
            gsap.fromTo(cartBubble,
              { scale: 0.5 },
              { scale: 1, duration: 0.6, ease: SPRING, overwrite: 'auto' }
            );
          }
        }
      });
    });
    observer.observe(cartBubble, { attributes: true, attributeFilter: ['class'] });
  }

  // ─── 7. LIFESTYLE GRID IMAGE HOVER ────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var wrapper = closest(e.target, '.lifestyle-grid__image-wrapper');
      if (!wrapper) return;
      var img = wrapper.querySelector('img');
      if (img) gsap.to(img, { scale: 1.04, duration: 0.5, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var wrapper = closest(e.target, '.lifestyle-grid__image-wrapper');
      if (!wrapper) return;
      var img = wrapper.querySelector('img');
      if (img) gsap.to(img, { scale: 1, duration: 0.6, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 8. COLLECTION SHOWCASE PRODUCT CARDS ─────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var card = closest(e.target, '.cs-product-card, .csl-product-card');
      if (!card) return;
      gsap.to(card, { y: -4, duration: 0.3, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var card = closest(e.target, '.cs-product-card, .csl-product-card');
      if (!card) return;
      gsap.to(card, { y: 0, duration: 0.35, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 9. SHOWCASE SLIDER ARROWS ────────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var arrow = closest(e.target, '.cs-arrow, .csl-arrow');
      if (!arrow) return;
      gsap.to(arrow, { scale: 1.1, duration: 0.2, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var arrow = closest(e.target, '.cs-arrow, .csl-arrow');
      if (!arrow) return;
      gsap.to(arrow, { scale: 1, duration: 0.3, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 10. TESTIMONIAL CARDS ────────────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var card = closest(e.target, '.tm-card');
      if (!card) return;
      gsap.to(card, { y: -3, duration: 0.3, ease: SMOOTH, overwrite: 'auto' });
      var img = card.querySelector('.tm-card__img');
      if (img) gsap.to(img, { scale: 1.03, duration: 0.4, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var card = closest(e.target, '.tm-card');
      if (!card) return;
      gsap.to(card, { y: 0, duration: 0.35, ease: SMOOTH, overwrite: 'auto' });
      var img = card.querySelector('.tm-card__img');
      if (img) gsap.to(img, { scale: 1, duration: 0.5, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 11. PRODUCT CARD OVERLAY (Collection Grid) ───────
  // Enhanced hover: image scale, gradient deepens, text slides up.
  // Works alongside existing CSS transitions (GSAP overrides inline).
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var card = closest(e.target, '.product-card-overlay');
      if (!card) return;
      var img = card.querySelector('.product-card-overlay__image');
      var gradient = card.querySelector('.product-card-overlay__gradient');
      var title = card.querySelector('.product-card-overlay__title');
      var price = card.querySelector('.product-card-overlay__price');

      if (img) gsap.to(img, { scale: 1.06, duration: 0.5, ease: SMOOTH, overwrite: 'auto' });
      if (gradient) gsap.to(gradient, { opacity: 0.85, duration: 0.4, ease: SMOOTH, overwrite: 'auto' });
      if (title) gsap.to(title, { y: -4, duration: 0.3, ease: SNAP, overwrite: 'auto' });
      if (price) gsap.to(price, { y: -4, duration: 0.3, ease: SNAP, delay: 0.05, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var card = closest(e.target, '.product-card-overlay');
      if (!card) return;
      var img = card.querySelector('.product-card-overlay__image');
      var gradient = card.querySelector('.product-card-overlay__gradient');
      var title = card.querySelector('.product-card-overlay__title');
      var price = card.querySelector('.product-card-overlay__price');

      if (img) gsap.to(img, { scale: 1, duration: 0.6, ease: SMOOTH, overwrite: 'auto' });
      if (gradient) gsap.to(gradient, { opacity: 0.6, duration: 0.4, ease: SMOOTH, overwrite: 'auto' });
      if (title) gsap.to(title, { y: 0, duration: 0.3, ease: SMOOTH, overwrite: 'auto' });
      if (price) gsap.to(price, { y: 0, duration: 0.3, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 12. COLLECTION NAV PILLS ─────────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var pill = closest(e.target, '.collection-nav-pills__link');
      if (!pill || pill.classList.contains('collection-nav-pills__link--active')) return;
      gsap.to(pill, { scale: 1.03, duration: 0.2, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var pill = closest(e.target, '.collection-nav-pills__link');
      if (!pill) return;
      gsap.to(pill, { scale: 1, duration: 0.25, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 13. FILTER BUTTON ────────────────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var btn = closest(e.target, '.js-drawer-open-collection-filters');
      if (!btn) return;
      gsap.to(btn, { y: -1, duration: 0.2, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var btn = closest(e.target, '.js-drawer-open-collection-filters');
      if (!btn) return;
      gsap.to(btn, { y: 0, duration: 0.25, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 14. PAGINATION LINKS ─────────────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var link = closest(e.target, '.pagination .page, .pagination .prev, .pagination .next');
      if (!link) return;
      gsap.to(link, { scale: 1.1, duration: 0.2, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var link = closest(e.target, '.pagination .page, .pagination .prev, .pagination .next');
      if (!link) return;
      gsap.to(link, { scale: 1, duration: 0.25, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 15. PDP GALLERY THUMBNAILS ───────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var thumb = closest(e.target, '.media-gallery__thumb');
      if (!thumb) return;
      gsap.to(thumb, { scale: 1.05, duration: 0.2, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var thumb = closest(e.target, '.media-gallery__thumb');
      if (!thumb) return;
      gsap.to(thumb, { scale: 1, duration: 0.3, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 16. PDP GALLERY NAV ARROWS ───────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var arrow = closest(e.target, '.media-gallery__nav');
      if (!arrow) return;
      gsap.to(arrow, { scale: 1.1, duration: 0.2, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var arrow = closest(e.target, '.media-gallery__nav');
      if (!arrow) return;
      gsap.to(arrow, { scale: 1, duration: 0.3, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 17. PDP VARIANT SWATCH/BUTTON HOVER ──────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var swatch = closest(e.target, '.element-radio, .element-swatch');
      if (!swatch) return;
      gsap.to(swatch, { scale: 1.05, duration: 0.2, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var swatch = closest(e.target, '.element-radio, .element-swatch');
      if (!swatch) return;
      gsap.to(swatch, { scale: 1, duration: 0.25, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

  // ─── 18. VARIANT SELECTION PULSE ──────────────────────
  document.addEventListener('change', function (e) {
    if (!e.target.matches('input[type="radio"]')) return;
    var label = e.target.closest('.element-radio, .element-swatch');
    if (!label) return;
    gsap.fromTo(label,
      { scale: 0.95 },
      { scale: 1, duration: 0.4, ease: SPRING, overwrite: 'auto' }
    );
  }, true);

  // ─── 19. QUANTITY +/- PRESS FEEDBACK ──────────────────
  document.addEventListener('click', function (e) {
    var qtyBtn = closest(e.target, '.js-qty__adjust, .element-quantity-selector .element-button');
    if (!qtyBtn) return;
    gsap.fromTo(qtyBtn,
      { scale: 0.9 },
      { scale: 1, duration: 0.4, ease: SPRING, overwrite: 'auto' }
    );
  }, true);

  // ─── 20. TEXT COLUMN IMAGE HOVER ──────────────────────
  if (!isTouch) {
    document.addEventListener('mouseenter', function (e) {
      var wrap = closest(e.target, '.text-column__grid .image-wrap');
      if (!wrap) return;
      var img = wrap.querySelector('img');
      if (img) gsap.to(img, { scale: 1.04, duration: 0.4, ease: SMOOTH, overwrite: 'auto' });
    }, true);

    document.addEventListener('mouseleave', function (e) {
      var wrap = closest(e.target, '.text-column__grid .image-wrap');
      if (!wrap) return;
      var img = wrap.querySelector('img');
      if (img) gsap.to(img, { scale: 1, duration: 0.5, ease: SMOOTH, overwrite: 'auto' });
    }, true);
  }

})();
